import React from 'react';
import Button from '../../components/Button/Button';
import SEO from '../../components/SEO/SEO';
import OptimizedImage from '../../components/OptimizedImage/OptimizedImage';
import FacebookFeed from '../../components/FacebookFeed/FacebookFeed';
import { ADDRESS, OPENING_HOURS, CONTACT_INFO } from '../../data/constants';
import { getLocalBusinessStructuredData } from '../../data/structuredData';
import { 
  User, 
  Stethoscope, 
  PersonStanding, 
  Brain, 
  Heart, 
  Dumbbell,
  Bone,
  Venus,
  SmilePlus,
  Layers
} from 'lucide-react';
import './Home.css';

const Home: React.FC = () => {
  return (
    <>
      <SEO
        title="Fizjoterapia i Rehabilitacja Recz"
        description="Fizjoterapia Recz - Gabinet Fizjoterapii i Rehabilitacji w Reczu. Fizjofit P.G. Pabich. Umów wizytę u Profesjonalnych i doświadczonych Fizjoterapeutów!"
        keywords="fizjoterapia recz, rehabilitacja recz, fizjoterapeuta w reczu, grzegorz pabich, masaże, leczenie kręgosłupa, problem z kręgosłupem, fizykoterapia, recz"
        canonical="/"
        structuredData={getLocalBusinessStructuredData()}
      />
      <div className="home">
        <section className="hero">
          <div className="hero-content">
            <h1>Fizjoterapia i Rehabilitacja</h1>
            <h2>Gabinet Fizjofit P.G. Pabich</h2>
            <p>Profesjonalna opieka fizjoterapeutyczna w Reczu</p>
            <div className="hero-buttons">
              <Button href="/kontakt" variant="primary" size="large">
                Umów wizytę
              </Button>
              <Button href="/oferta" variant="outline" size="large">
                Poznaj ofertę
              </Button>
            </div>
          </div>
        </section>

        <section className="photo-gallery">
          <div className="gallery-container">
            <div className="gallery-item">
              <div className="gallery-image">
                <OptimizedImage 
                  src="/img/DSC_5018.jpg" 
                  alt="Ból kręgosłupa - holistyczne podejście" 
                  width={600}
                  height={400}
                  priority={true}
                />
              </div>
              <div className="gallery-caption">
                <h3>Ból kręgosłupa - holistyczne podejście</h3>
                <p>Odnajdujemy przyczynę dolegliwości, nie tylko objawy. Ból karku, szyi, piersiowy i lędźwiowy</p>
              </div>
            </div>
            
            <div className="gallery-item">
              <div className="gallery-image">
                <OptimizedImage 
                  src="/img/DSC_5093.JPG" 
                  alt="Rehabilitacja pooperacyjna" 
                  width={600}
                  height={400}
                />
              </div>
              <div className="gallery-caption">
                <h3>Rehabilitacja pooperacyjna</h3>
                <p>Artroskopowe leczenie stawów kolanowych, skokowych i barkowych</p>
              </div>
            </div>
            
            <div className="gallery-item">
              <div className="gallery-image">
                <OptimizedImage 
                  src="/img/DSC_5119.jpg" 
                  alt="Urazy sportowe i przeciążenia" 
                  width={600}
                  height={400}
                />
              </div>
              <div className="gallery-caption">
                <h3>Urazy sportowe i przeciążenia</h3>
                <p>Skuteczne leczenie skręceń, naderwań i przeciążeń mięśniowych</p>
              </div>
            </div>
            
            <div className="gallery-item">
              <div className="gallery-image">
                <OptimizedImage 
                  src="/img/DSC_5206.jpg" 
                  alt="Profilaktyka i ruch" 
                  width={600}
                  height={400}
                />
              </div>
              <div className="gallery-caption">
                <h3>Profilaktyka i ruch</h3>
                <p>Lepiej zapobiegać niż leczyć - treningi profilaktyczne i rehabilitacyjne</p>
              </div>
            </div>
          </div>
        </section>
        
        <section className="opening-hours-section">
          <div className="opening-hours-container">
            <h2><i className="far fa-clock"></i> Godziny otwarcia</h2>
            <div className="hours-table">
              {OPENING_HOURS.map((schedule, index) => (
                <div key={index} className="hours-row">
                  <span className="day">{schedule.day}</span>
                  <span className="hours">{schedule.hours}</span>
                </div>
              ))}
            </div>
            <p className="hours-note">* godziny wizyt ustalane indywidualnie</p>
            <Button href="/kontakt" variant="primary" size="large">
              Umów wizytę
            </Button>
          </div>
        </section>

        <section className="main-content">
          <div className="content-wrapper">
            <h2><i className="fas fa-heartbeat"></i> Fizjoterapia i Rehabilitacja w miejscowości Recz</h2>
            
            <div className="content-text">
              <p>
                Nasz Gabinet Fizjoterapii i Rehabilitacji dostępny jest przy głównej ulicy Recza ({ADDRESS.street}). 
                Jest nam niezmiernie miło zaprosić Państwa do skorzystania z naszych usług: <strong>fizjoterapia</strong>, 
                terapia manualna często wykorzystywana przy bólach kręgosłupa, <strong>rehabilitacja</strong> pourazowa - 
                pomożemy Tobie wrócić do pełni sił! Od 2017 roku jesteśmy członkami{' '}
                <a href="https://kif.info.pl/" target="_blank" rel="noopener noreferrer" aria-label="Strona Krajowej Izby Fizjoterapeutów - otwiera się w nowym oknie">Krajowej Izby Fizjoterapeutów</a>.
              </p>
              
              <p>
                Korzystając z usług fizjoterapeutów w Fizjofit P.G. Pabich w Reczu możesz być pewny, że trafiłeś we właściwe 
                ręce i już niedługo będziesz się cieszyć z pełni zdrowia. Wykwalifikowani fizjoterapeuci w miejscowości{' '}
                <strong>Recz</strong> wraz z odpowiednim nastawieniem Pacjenta, gwarantują sukces w powrocie do zdrowia!
              </p>
              
              <p>
                Fizjoterapia oraz rehabilitacja, jaką wykonujemy w naszym gabinecie to połączenie zdobywanej wiedzy 
                (zobacz nasze <a href="/certyfikaty" aria-label="Przejdź do strony z certyfikatami">Certyfikaty</a>), a także nowoczesnego sprzętu medycznego.
              </p>
            </div>

            <div className="location-info">
              <h3>Obszar obsługi - Fizjoterapia dla mieszkańców okolicznych miast</h3>
              <p>
                Zachęcamy do kontaktu również osoby spoza Recza. Fizjoterapia i rehabilitacja w naszym gabinecie będzie 
                świetnym wyborem dla mieszkańców pobliskich miast: np.{' '}
                <a href="/fizjoterapia-choszczno" aria-label="Fizjoterapia Choszczno - dedykowana strona">
                  <strong>Choszczno</strong>
                </a>, Barlinek, Kalisz Pomorski, Drawno, 
                Suchań, Stargard, Chociwel, Drawsko Pomorskie, Dolice, Pełczyce
              </p>
              <p>
                Mieszkańcy <strong>Choszczna</strong> mogą dojechać do naszego gabinetu w zaledwie 20 minut!{' '}
                <a href="/fizjoterapia-choszczno" aria-label="Dowiedz się więcej o fizjoterapii dla mieszkańców Choszczna">
                  Sprawdź szczegóły dotyczące fizjoterapii w pobliżu Choszczna
                </a>.
              </p>
            </div>

            <div className="treatment-approach">
              <h3>Nasze podejście do leczenia</h3>
              <p>
                W pracy fizjoterapeuty najważniejszy jest pacjent. Do każdego podchodzimy indywidualnie, aby jak najtrafniej 
                wskazać problem z jakim się do nas zgłosił. Za każdym razem zaczynamy od szczegółowego wywiadu i badania 
                pacjenta, co pozwala nam ustalić źródło problemu. Kolejnym krokiem jest wdrożenie terapii.
              </p>
              
              <p>
                Oferujemy szeroką gamę usług fizjoterapeutycznych oraz rehabilitacyjnych, m.in. <strong>masaż</strong>, 
                akupunktura, terapia manualna. Niestety siedzący tryb życia nie sprzyja naszemu kręgosłupowi.{' '}
                <strong>Ból kręgosłupa</strong> towarzyszy nam co raz częściej, dlatego bardzo istotne jest jego prawidłowe 
                utrzymanie w dobrej kondycji.
              </p>
              
              <p>
                Prowadzimy również Zajęcia Fitness i Treningi Personalne, które pomogą Ci wzmocnić całe ciało oraz 
                poprawić mobilność kręgosłupa.
              </p>
            </div>
          </div>
        </section>

        <section className="services">
          <h2>Pomagamy w dolegliwościach takich jak</h2>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon">
                <User size={32} />
              </div>
              <h3>Ból kręgosłupa</h3>
              <p>Ból karku, odcinka szyjnego, piersiowego i lędźwiowego. Holistyczne podejście - odnajdujemy przyczynę dolegliwości, nie tylko objawy.</p>
            </div>
            
            <div className="service-card">
              <div className="service-icon">
                <Stethoscope size={32} />
              </div>
              <h3>Rehabilitacja pooperacyjna</h3>
              <p>Artroskopowe leczenie stawu kolanowego, skokowego i barkowego. Kompleksowa rehabilitacja po urazach i operacjach.</p>
            </div>
            
            <div className="service-card">
              <div className="service-icon">
                <PersonStanding size={32} />
              </div>
              <h3>Urazy sportowe</h3>
              <p>Skręcenia, naderwania, przeciążenia. Specjalistyczna opieka dla sportowców i osób aktywnych fizycznie.</p>
            </div>
            
            <div className="service-card">
              <div className="service-icon">
                <Brain size={32} />
              </div>
              <h3>Ból głowy</h3>
              <p>Migreny, bóle napięciowe. Skuteczne leczenie przyczyn bólów głowy poprzez terapię manualną.</p>
            </div>
            
            <div className="service-card">
              <div className="service-icon">
                <Heart size={32} />
              </div>
              <h3>Problemy trawienne</h3>
              <p>Zaparcia, biegunki, refluks. Terapia wisceralna pomaga w problemach z układem pokarmowym.</p>
            </div>
            
            <div className="service-card">
              <div className="service-icon">
                <Dumbbell size={32} />
              </div>
              <h3>Profilaktyka i ruch</h3>
              <p>Lepiej zapobiegać niż leczyć. Treningi profilaktyczne i rehabilitacyjne dla utrzymania zdrowia.</p>
            </div>

            <div className="service-card">
              <div className="service-icon">
                <Bone size={32} />
              </div>
              <h3>Bóle stawowe</h3>
              <p>Bolące barki, biodra, kolana lub łokcie? Łączymy terapię manualną i ćwiczenia odciążające stawy.</p>
            </div>

            <div className="service-card">
              <div className="service-icon">
                <Venus size={32} />
              </div>
              <h3>Problemy uroginekologiczne</h3>
              <p>Terapia dna miednicy po porodach, przy nietrzymaniu moczu czy bolesności okolicy miednicy.</p>
            </div>

            <div className="service-card">
              <div className="service-icon">
                <Brain size={32} />
              </div>
              <h3>Stany po udarze i problemy neurologiczne</h3>
              <p>Reedukacja chodu, poprawa równowagi i praca nad spastycznością po udarze oraz w innych schorzeniach neurologicznych.</p>
            </div>

            <div className="service-card">
              <div className="service-icon">
                <SmilePlus size={32} />
              </div>
              <h3>Staw skroniowo-żuchwowy</h3>
              <p>Bóle żuchwy, trzaski, zaciskanie zębów. Terapia manualna i autoterapie przy zaburzeniach SSŻ.</p>
            </div>

            <div className="service-card">
              <div className="service-icon">
                <Layers size={32} />
              </div>
              <h3>Dyskopatia i „wypadający” dysk</h3>
              <p>Praca nad zmniejszeniem ucisku na nerwy, poprawą ruchomości i nauką bezpiecznych wzorców ruchowych.</p>
            </div>
          </div>
        </section>

        <section className="facebook-section">
          <div className="facebook-section-content">
            <div className="facebook-section-text">
              <h2>Śledź nas na Facebooku</h2>
              <p>
                Bądź na bieżąco z najnowszymi informacjami o fizjoterapii, poradami zdrowotnymi 
                i aktualnościami z naszego gabinetu. Udostępniamy cenne wskazówki dotyczące 
                profilaktyki oraz ćwiczeń rehabilitacyjnych.
              </p>
            </div>
            <div className="facebook-section-feed">
              <FacebookFeed 
                width={340} 
                height={500} 
                showPosts={3}
                hideCover={false}
                smallHeader={true}
                adaptContainerWidth={true}
              />
            </div>
          </div>
        </section>

        <section className="cta-section">
          <div className="cta-content">
            <h2>Potrzebujesz pomocy fizjoterapeutycznej?</h2>
            <p>Skontaktuj się z nami i umów się na pierwszą wizytę. Pomożemy Ci wrócić do pełnej sprawności!</p>
            <div className="cta-buttons">
              <Button href={`tel:${CONTACT_INFO[0].phone.replace(/\s/g, '')}`} variant="primary" size="large">
                <i className="fas fa-phone"></i> Zadzwoń teraz
              </Button>
              <Button href="/kontakt" variant="outline" size="large">
                Napisz do nas
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;
